<template>
  <ModalDialog
    ref="dialog"
    title="Xero Configuration"
    icon="fa-cog"
    :submitting="submitting"
    submitTitle="Update"
    sizeClass="modal-xl"
    :submitDisabled="false"
    :scrollable="true"
    @closed="onClosed"
    @submitted="onSubmitted">

    <FormGroup v-bind:form="xeroForm" :labelColumnSize="3">
      <LoadingPanel ref="loadingPanel" @update="loading = $event" />
      <TabbedBox v-if="!loading">
         <template v-slot:tablist>
          <Tab :first="true" tabid="tab-config">
            <i class="far fa-cog"></i> Sync Config
          </Tab>
          <Tab tabid="tab-accounts">
            <i class="far fa-money-check-alt"></i> Accounts
          </Tab>
          <Tab tabid="tab-taxrates">
            <i class="far fa-donate"></i> Tax Rates
          </Tab>
          <Tab tabid="tab-syncerrors" :errorCount="syncErrorList.length">
            <i class="far fa-exclamation-triangle"></i> Sync Errors
          </Tab>
        </template>

        <template v-slot:tabcontent>
          <TabPage :first="true" tabid="tab-config">
            <ContentTitle>Xero Sync Direction Configuration</ContentTitle>
            <NotificationBar type="info">
              <NotificationBarTitle>Xero Syncing</NotificationBarTitle>
              <ul>
                <li>Objects in Synergy are considered the <strong>source of truth</strong>, meaning all data and updates should originate and be modified within Synergy where possible.</li>
                <li>Only payment allocations to invoices within Xero will be imported from Xero into the Synergy system.</li>
                <li>Locking accounts in Xero will cause sync errors and fail when updating older items. For example, allocating a credit note created before the lock end date.</li>
                <li>Synergy cannot link to archived contacts in Xero or create new contacts with matching names to archived contacts.</li>
              </ul>              
            </NotificationBar>

            <StaticDataTable class="static-table-list">
              <template v-slot:header>
                <tr>
                  <th width="40%">Setting</th>
                  <th width="20%">Read From Xero</th>
                  <th width="20%">Write To Xero</th>
                  <th width="20%"><Tooltip helpStyle label="Items in Synergy or Xero that have a creation or update date earlier than this will not be synced">Sync No Earlier Than</Tooltip></th>
                </tr>
              </template>
              <template v-slot:body>
                <tr>
                  <td>Contacts<br />
                  <small>Note: Contacts <strong>must</strong> be synced with Xero to sync other items below</small></td>
                  <td>-</td>
                  <td><FormSwitch :booleanMode="true" v-model="xeroOptions[XeroOptionTypes.contactWriteSyncEnabled]" /></td>
                  <td><FormDate v-model="xeroOptions[XeroOptionTypes.contactSyncStartDate]" /></td>
                </tr>
                <tr>
                  <td>Invoices<br />
                  <small>Note: Invoices must be synced with Xero to sync payment allocations</small></td>
                  <td>-</td>
                  <td><FormSwitch :booleanMode="true" v-model="xeroOptions[XeroOptionTypes.invoiceWriteSyncEnabled]" /></td>
                  <td><FormDate v-model="xeroOptions[XeroOptionTypes.invoiceSyncStartDate]" /></td>
                </tr>
                <tr>
                  <td>Credit Notes</td>
                  <td>-</td>
                  <td><FormSwitch :booleanMode="true" v-model="xeroOptions[XeroOptionTypes.creditNoteWriteSyncEnabled]" /></td>
                  <td><FormDate v-model="xeroOptions[XeroOptionTypes.creditNoteSyncStartDate]" /></td>
                </tr>
                <tr>
                  <td>Payments</td>
                  <td><FormSwitch :booleanMode="true" v-model="xeroOptions[XeroOptionTypes.invoicePaymentReadSyncEnabled]" /></td>
                  <td><FormSwitch :booleanMode="true" v-model="xeroOptions[XeroOptionTypes.invoicePaymentWriteSyncEnabled]" /></td>
                  <td><FormDate v-model="xeroOptions[XeroOptionTypes.invoicePaymentSyncStartDate]" /></td>
                </tr>
                <tr>
                  <td>Overpayments</td>
                  <td><FormSwitch :booleanMode="true" v-model="xeroOptions[XeroOptionTypes.overpaymentReadSyncEnabled]" /></td>
                  <td><FormSwitch :booleanMode="true" v-model="xeroOptions[XeroOptionTypes.overpaymentWriteSyncEnabled]" /></td>
                  <td><FormDate v-model="xeroOptions[XeroOptionTypes.overpaymentSyncStartDate]" /></td>
                </tr>
              </template>
            </StaticDataTable>            

          </TabPage>

          <TabPage tabid="tab-accounts">
            <ContentTitle>Xero Accounts Setup</ContentTitle>
            <FormRow v-for="accountConfig of accountConfigList" :key="accountConfig.accountType" :label="accountConfig.name" :labelColumnSize="3">
              <FormSearchableSelect v-model="accountConfig.xeroAccountID" :options="xeroAccountLookup" />
            </FormRow>
          </TabPage>

          <TabPage tabid="tab-taxrates">
            <ContentTitle>Tax Rates Setup</ContentTitle>
            <FormRow v-for="taxRate of taxRateConfigList" :key="taxRate.taxRateId" :label="taxRate.name" :labelColumnSize="3">
              <FormSearchableSelect v-model="taxRate.xeroTaxTypeCode" :options="xeroTaxRateLookup" />
            </FormRow>
          </TabPage>

          <TabPage tabid="tab-syncerrors">
            <ContentTitle>Sync Error Message List</ContentTitle>

            <StaticDataTable class="static-table-list">
              <template v-slot:header>
                <tr>
                  <th width="10%">Type</th>
                  <th width="15%">Name</th>
                  <th width="50%">Message</th>
                  <th width="15%">Date</th>
                  <th width="10%">Action</th>
                </tr>
              </template>
              <template v-slot:body>
                <tr v-for="syncError of syncErrorList" :key="`${syncError.linkType}-${syncError.linkId}`">
                  <template v-if="syncError.linkType == LinkTypes.contact">
                    <td>Contact</td>
                    <td><LinkContact :id="syncError.linkId" :name="syncError.name" /></td>
                  </template>
                  <template v-if="syncError.linkType == LinkTypes.invoice">
                    <td>Invoice</td>
                    <td><LinkInvoice :id="syncError.linkId" :name="syncError.name" /></td>
                  </template>
                  <template v-if="syncError.linkType == LinkTypes.overPayment">
                    <td>Over Payment</td>
                    <td><LinkOverPayment :id="syncError.linkId" :name="syncError.name" /></td>
                  </template>
                  <template v-if="syncError.linkType == LinkTypes.paymentReceived">
                    <td>Payment Received</td>
                    <td><LinkPaymentReceived :id="syncError.linkId" :name="syncError.name" /></td>
                  </template>
                  <template v-if="syncError.linkType == LinkTypes.creditNote">
                    <td>Credit Note</td>
                    <td><LinkCreditNote :id="syncError.linkId" :name="syncError.name" /></td>
                  </template>
                  <td>{{syncError.message}}</td>
                  <td>{{formatDateTime(syncError.syncErrorDate)}}</td>
                  <td>
                    <ActionDropMenu :tableAction="true" :forceDropdown="true">
                      <DropdownMenuItem v-if="syncError.isMessageClearable" icon="fa-times" @click="clearSyncError(syncError)">Clear Message</DropdownMenuItem>
                    </ActionDropMenu>
                  </td>
                </tr>
              </template>
              <template v-slot:empty v-if="syncErrorList.length == 0">
                No sync errors in the system
              </template>
            </StaticDataTable>
            
          </TabPage>
        </template>
      </TabbedBox>
      
    </FormGroup>
  </ModalDialog>
</template>

<style>
</style>

<script>
import _ from 'lodash'
import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import modalDialog from '@/mixins/modalDialog'
import IntegrationXeroAPIService from '@/integrations/xero/services/apiService'

import { XeroOptionTypes } from '@/integrations/xero/types'
import { LinkTypes } from '@/store/types'

export default {
  name: "XeroConfigDialog",
  mixins: [
    modalDialog,
    form
  ],
  components: {
    ModalDialog,
  },
  props: {
    
  },
  data() {
    return {
      xeroForm: {},
      loading: true,
      submitting: false,
      xeroOptions: {},
      xeroAccountsList: [],
      xeroTaxRatesList: [],

      taxRateConfigList: [],
      accountConfigList: [],

      syncErrorList: []
    }
  },
  created() {
    this.XeroOptionTypes = XeroOptionTypes
    this.LinkTypes = LinkTypes
  },
  mounted() {
  },
  computed: {
    xeroAccountLookup() {
      return _.concat(
        { name: 'None', value: null },
        this.xeroAccountsList.map(xeroAccount => {
          return { name: `${xeroAccount.code} - ${xeroAccount.name}`, value: xeroAccount.xeroAccountID }
        })
      )
    },
    xeroTaxRateLookup() {
      return _.concat(
        { name: 'None', value: null },
        this.xeroTaxRatesList.map(xeroTaxRate => {
          return { name: xeroTaxRate.name, value: xeroTaxRate.taxType }
        })
      )
    }
  },
  methods: {
    show(integrationData) {
      // Reset form
      this.xeroForm = {}
      this.submitting = false

      this.requestData()

      return modalDialog.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(false)
    },
    onSubmitted() {
      this.submitting = true
      this.xeroForm = this.clearFormResponse()

      // Fill in the extra details
      this.accountConfigList.forEach(accountConfig => {
        accountConfig.xeroAccountCode = null

        let xeroAcc = this.xeroAccountsList.find(acc => acc.xeroAccountID == accountConfig.xeroAccountID)
        if(xeroAcc)
          accountConfig.xeroAccountCode = xeroAcc.code
      })

      var updateData = {
        accountList: this.accountConfigList,
        taxRateList: this.taxRateConfigList,
        options: this.xeroOptions
      }
      
      var xeroService = new IntegrationXeroAPIService(this.$api)
      xeroService.updateConfig(updateData)
      .then(response => {
        this.modalClosed(true)
        this.hide()
      })
      .catch(err => {
        this.$data.xeroForm = this.processFormResponse(err)
      })
      .finally(() => {
        this.submitting = false
      })
    },
    reloadData() {
      this.requestData()
    },
    reloadSyncErrorList() {
      var xeroService = new IntegrationXeroAPIService(this.$api)
      xeroService.getSyncErrorList()
      .then(response => {
        this.syncErrorList = response.data
      })
      .catch(err => {
        this.displayNetworkError(err, 'Error loading sync error list')
      })
    },
    requestData() {
      var xeroService = new IntegrationXeroAPIService(this.$api)

      this.$refs.loadingPanel.startLoading()
      Promise.all([
        xeroService.getConfig(),
        xeroService.getXeroAccountList(),
        xeroService.getXeroTaxRateList(),
        xeroService.getAccountConfigList(),
        xeroService.getTaxRateConfigList(),
        xeroService.getSyncErrorList()
      ]).then(results => {
        this.xeroOptions = results[0].data.options || {}
        this.xeroAccountsList = results[1].data
        this.xeroTaxRatesList = results[2].data

        this.accountConfigList = results[3].data
        this.taxRateConfigList = results[4].data

        this.syncErrorList = results[5].data
      })
      .catch(err => {
        this.$refs.loadingPanel.setError('Error accessing Xero configuration data', err)
      })
      .finally(() => {
        this.$refs.loadingPanel.finishedLoading()
      })
    },
    clearSyncError(syncError) {
      var xeroService = new IntegrationXeroAPIService(this.$api)

      this.confirmDeleteMessage('Clear Sync Message?', 'This will clear the message from appearing in the sync error list. This will not remove, update or resync any data')
      .then(confirm => {
        if(confirm) {
          xeroService.clearSyncErrorMessage(syncError)
          .then(response => {
            this.successToast('Message Cleared')
            this.reloadSyncErrorList()
          })
          .catch(err => {
            this.displayNetworkError(err, 'Error clearing message')
          })
        }
      })
    }
  }
}
</script>